"use client"

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Megaphone } from 'lucide-react'
import { toast } from 'sonner'
import { getPageHeader } from '../../constants'
import { useFeedbackItems, useVoteFeedbackItem, type FeedbackItem } from '../../hooks/use-feedback'
import { FeedbackListCard } from './feedback-list-card'
import { FeedbackPromptsSection } from './feedback-prompts-section'
import { Button } from '../ui/button'
import { Card, CardContent } from '../ui/card'
import { PageContainer } from '../ui/page-container'
import { PageHeader } from '../ui/page-header'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import { PageHeaderStack } from '@/components/navigation/page-header-stack'
import { ListSortMenu } from '@/components/filters/list-sort-menu'
import { PaneResponsiveCardGrid } from '@/components/layout/pane-responsive-card-grid'
import { PaneListEmptyState } from '@/components/layout/pane-list-empty-state'
import { DashboardSectionSkeleton } from '@/components/dashboard/dashboard-section-skeleton'
import { formatFeedbackKind } from '@/lib/feedback-display'
import { useListFilterBar } from '@/hooks/use-list-filter-bar'
import {
  computeFeedbackKindCounts,
  FEEDBACK_KIND_FILTER_DEFINITIONS,
  FEEDBACK_SORT_OPTIONS,
  type FeedbackKindFilterState,
  type FeedbackSort,
} from '@/lib/list-filters/feedback-kind'

export type FeedbackPageProps = {
  /** Hands a prompt-answer kickoff message to the chat composer (absent when agent UI is off). */
  onStartConversation?: (message: string) => void
}

export function FeedbackPage({ onStartConversation }: FeedbackPageProps = {}) {
  const [kindFilter, setKindFilter] = useState<FeedbackKindFilterState['kind']>('')
  const [sort, setSort] = useState<FeedbackSort>('votes')

  const { items: allItems, isLoading, error } = useFeedbackItems({ sort })
  const { voteItem, isVoting } = useVoteFeedbackItem()

  const counts = useMemo(() => computeFeedbackKindCounts(allItems), [allItems])
  const filterCtx = useMemo(() => ({ counts }), [counts])

  const feedbackFilters: FeedbackKindFilterState = useMemo(
    () => ({ search: '', kind: kindFilter }),
    [kindFilter],
  )

  const onFiltersChange = useCallback((next: FeedbackKindFilterState) => {
    setKindFilter(next.kind)
  }, [])

  const clearFilters = useCallback(() => {
    setKindFilter('')
    setSort('votes')
  }, [])

  const { filterBar } = useListFilterBar({
    listId: 'feedback',
    definitions: FEEDBACK_KIND_FILTER_DEFINITIONS,
    filters: feedbackFilters,
    onFiltersChange,
    ctx: filterCtx,
    onClearAll: clearFilters,
    hasActiveExtras: sort !== 'votes',
    toolbar: {
      sortMenu: (
        <ListSortMenu
          options={[...FEEDBACK_SORT_OPTIONS]}
          value={sort}
          onValueChange={(value) => setSort(value as FeedbackSort)}
          ariaLabel="Sort feedback"
        />
      ),
    },
  })

  const items = useMemo(() => {
    if (!kindFilter || kindFilter === 'all') return allItems
    return allItems.filter((item) => item.kind === kindFilter)
  }, [allItems, kindFilter])

  useEffect(() => {
    if (kindFilter && kindFilter !== 'all' && (counts[kindFilter] ?? 0) === 0) {
      setKindFilter('')
    }
  }, [kindFilter, counts])

  const handleToggleVote = (item: FeedbackItem) => {
    voteItem(
      { itemId: item.id, vote: item.my_vote !== true },
      {
        onError: (err: Error) => {
          toast.error(err.message || 'Failed to update vote')
        },
      },
    )
  }

  if (error) {
    return (
      <PageContainer>
        <div className="py-12 text-center">
          <p className="text-destructive">Error loading feedback: {(error as Error).message}</p>
        </div>
      </PageContainer>
    )
  }

  const { title, description } = getPageHeader(
    '/feedback',
    'Feedback',
    'Bugs, ideas, and requests from alpha testers — upvote what matters to you',
  )

  return (
    <PageContainer>
      <PageHeaderStack>
        <PageHeader
          leading={<PageHeaderLeading />}
          title={title}
          description={description}
          icon={<Megaphone className="h-5 w-5" />}
        />

        {filterBar}
      </PageHeaderStack>

      <FeedbackPromptsSection onStartConversation={onStartConversation} />

      {isLoading ? (
        <DashboardSectionSkeleton cards={4} grid />
      ) : items.length === 0 ? (
        <Card>
          <CardContent>
            <PaneListEmptyState
              icon={<Megaphone aria-hidden />}
              title="No feedback yet"
              description={
                kindFilter && kindFilter !== 'all'
                  ? `No ${formatFeedbackKind(kindFilter).toLowerCase()} items yet`
                  : 'Tell the assistant about a bug, an idea, or anything you would change — it lands here for others to upvote.'
              }
              action={
                kindFilter && kindFilter !== 'all' ? (
                  <Button type="button" variant="outline" onClick={() => setKindFilter('')}>
                    Show all feedback
                  </Button>
                ) : undefined
              }
            />
          </CardContent>
        </Card>
      ) : (
        <PaneResponsiveCardGrid>
          {items.map((item) => (
            <FeedbackListCard
              key={item.id}
              item={item}
              className="h-full"
              onToggleVote={handleToggleVote}
              isVoting={isVoting}
            />
          ))}
        </PaneResponsiveCardGrid>
      )}
    </PageContainer>
  )
}
